// ============================================================
// pdfWriter.js — turns the sheets from sheetBuilder into a vector PDF
//
// Each sheet SVG goes onto its own page with svg2pdf, so lines stay
// vector and all text is real (selectable/searchable) Helvetica.
// ============================================================

import jsPDF from 'jspdf';
import 'svg2pdf.js';          // adds pdf.svg()
import { buildSheets } from './sheetBuilder';
import { loadEquipmentArt } from './equipmentArt';

// Loads the equipment drawings the plan needs, then builds the sheets.
// Use this for the preview too, so preview and PDF are identical.
export async function prepareSheets(project, settings) {
  await loadEquipmentArt((project.placed || []).filter((p) => !p.isRoom).map((p) => p.id));
  return buildSheets(project, settings);
}

const parseSvg = (str) => {
  const doc = new DOMParser().parseFromString(str, 'image/svg+xml');
  const err = doc.querySelector('parsererror');
  if (err) throw new Error('Drawing sheet could not be built (bad SVG): ' + err.textContent.slice(0, 120));
  return doc.documentElement;
};

// Returns a jsPDF document (call .save(name) or .output('blob'))
export async function buildDrawingPdf(project, settings) {
  const built = await prepareSheets(project, settings);
  const { pages, facts } = built;
  const first = pages[0];
  const pdf = new jsPDF({ unit: 'pt', format: [first.w, first.h], orientation: 'landscape', compress: true });

  // svg2pdf measures some things through the live DOM, so each sheet is
  // mounted off-screen while it renders, then removed.
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-99999px;top:0;width:0;height:0;overflow:hidden;';
  document.body.appendChild(host);
  try {
    for (let i = 0; i < pages.length; i++) {
      const pg = pages[i];
      if (i > 0) pdf.addPage([pg.w, pg.h], 'landscape');
      const el = parseSvg(pg.svg);
      host.appendChild(el);
      // eslint-disable-next-line no-await-in-loop
      await pdf.svg(el, { x: 0, y: 0, width: pg.w, height: pg.h });
      host.removeChild(el);
      pg.links.forEach((lk) => pdf.link(lk.x, lk.y, lk.w, lk.h, { url: lk.url }));
    }
  } finally {
    document.body.removeChild(host);
  }

  pdf.setProperties({
    title: `${facts.project || 'Shop layout'} — Equipment Layout Plan`,
    subject: `Drawing ${facts.drawingNo} rev ${facts.revision}`,
    creator: 'ShopBuilder Pro',
    author: facts.preparedBy || 'ShopBuilder Pro',
  });
  return { pdf, built };
}

// File name like "Smith_Auto-SB-001-RevA.pdf"
export function drawingFileName(facts) {
  const base = (facts.project || 'ShopBuilder-Layout').replace(/[^a-z0-9\s\-_]/gi, '').trim().replace(/\s+/g, '_') || 'ShopBuilder-Layout';
  const dwg = facts.drawingNo.replace(/[^a-z0-9\-_]/gi, '');
  return `${base}-${dwg}-Rev${facts.revision.replace(/[^a-z0-9]/gi, '')}.pdf`;
}
